import React, { memo, useEffect, useState } from 'react'
import propTypes from 'prop-types'
import axios from 'axios'
import TextInput from '../../TextInput'
import Button from '../../Button'
import SearchResults from '../SearchResults'
import SelectImageType from '../SelectImageType'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import './styles.scss'

const SearchImageBank = ({ chooseImage, closeModal, imageTypes, setImageTypes }) => {
  const [searchString, setSearchString] = useState('')
  const [searchTitle, setSearchTitle] = useState('')
  const [results, setResults] = useState([])
  const [resultsTotal, setResultsTotal] = useState(0)
  const [filteredResults, setFilteredResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const apiUrl = process.env.REACT_APP_KUVAPANKKI_API_URL

  // this is used to enable search by pressing enter
  const searchRef = React.useRef(searchString)

  const getImageTypeLabel = (type) => {
    const imageTypeLabels = {
      ARASAAC: 'arasaac',
      KUVAKO: 'kuvako',
      Mulberry: 'mulberry',
      Piirroskuva: 'drawing',
      Sclera: 'sclera',
      Toisto: 'toisto',
      Valokuva: 'photo',
      Viittomakuva: 'sign',
    }
    return imageTypeLabels[type] || type
  }

  const setSearchTerm = (e) => {
    searchRef.current = e.target.value.toLowerCase()
    setSearchString(e.target.value.toLowerCase())
  }

  const searchDatabase = async (searchTerm) => {
    setMessage(null)
    if (searchTerm.length < 2) {
      setMessage('Tarkenna hakua!')
    } else {
      setSearchTitle('')
      setLoading(true)
      const res = await axios.get(`${apiUrl}/search/all/${searchTerm}?lang=fi`)
      if (res) {
        const data = res.data
        setResults(data)
        setSearchTitle(searchTerm)
        if (data.images.length === 0) {
          setMessage('Ei hakutuloksia. Kokeile toisella hakusanalla!')
        }
      }
      setLoading(false)
    }
  }

  useEffect(() => {
    const listener = (event) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        searchDatabase(`${searchRef.current}`)
      }
    }
    document.addEventListener('keydown', listener)
    return () => {
      document.removeEventListener('keydown', listener)
    }
  }, [])

  useEffect(() => {
    if (
      !results ||
      !results.images ||
      results.images.length === 0 ||
      imageTypes?.length === 0
    ) {
      setFilteredResults({ images: [] })
      setResultsTotal(0)
      return
    }

    const shallowCopy = { ...results }
    let filteredImages = shallowCopy.images
    let filteredTotal = 0

    if (imageTypes?.length < 8) {
      filteredImages = shallowCopy.images.filter((image) =>
        imageTypes.includes(getImageTypeLabel(image.type))
      )
      filteredTotal = filteredImages?.length || 0
    }

    setFilteredResults({ ...shallowCopy, images: filteredImages })
    setResultsTotal(filteredTotal)
  }, [results, imageTypes])

  return (
    <div className="search-container">
      <div className="search-input-and-buttons-container">
        <TextInput
          placeHolder="Etsi kuvapankista..."
          onChange={(e) => setSearchTerm(e)}
          name="Etsi kuvapankista"
          label="Etsi kuvapankista."
          showLabel={false}
          labelClassName="text-label-class"
          inputClassName="text-input-class"
          role="search"
        />
        <div className="search-buttons-container">
          <SelectImageType imageTypes={imageTypes} setImageTypes={setImageTypes} />
          <Button
            onClick={() => searchDatabase(searchString)}
            className="search-button"
            disabled={imageTypes && imageTypes.length === 0}
          >
            Hae
          </Button>
        </div>
      </div>
      {searchTitle && filteredResults?.images?.length > 0 && (
        <div>
          <h3 className="search-results-header">
            {searchTitle} ({resultsTotal})
          </h3>
        </div>
      )}
      {loading ? (
        <div className="spinner-content">
          <FontAwesomeIcon icon="spinner" spin className="spinner-icon" />
          <p>Ladataan...</p>
        </div>
      ) : (
        <></>
      )}
      {filteredResults?.images && filteredResults.images.length > 0 ? (
        <SearchResults
          key={`search-results-${filteredResults.images.length}-${imageTypes?.join(
            '-'
          )}`}
          results={filteredResults}
          closeModal={closeModal}
          chooseImage={chooseImage}
          isSearchTab={true}
          setResultsTotal={setResultsTotal}
        />
      ) : (
        <p className="no-results-text">
          {message
            ? message
            : imageTypes?.length === 0
            ? 'Valitse vähintään yksi kuvatyyppi.'
            : 'Ei hakutuloksia. Kokeile toisella hakusanalla.'}
        </p>
      )}
    </div>
  )
}

SearchImageBank.propTypes = {
  chooseImage: propTypes.func.isRequired,
  closeModal: propTypes.func.isRequired,
  imageTypes: propTypes.instanceOf(Object).isRequired,
  setImageTypes: propTypes.func.isRequired,
}

export default memo(SearchImageBank)
